import { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Select, Tag, Row, Col } from "antd";
import { FaSave } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { Flex } from "@chakra-ui/react";
import styled from "styled-components";
import { showSuccess, showError } from "infra/helpers/alerta";
import URL from "core/resources/urls";
import http from "infra/http";
import { Perfil, Permissao, PermissaoOption, PermissaoPerfil } from "./typings";

const { Item, useForm } = Form;

interface ModalFormPerfilProps {
  isOpen: boolean;
  perfilId: string | null;
  onClose: () => void;
  refetch: () => void;
}

function ModalFormPerfil({ isOpen, perfilId, onClose, refetch }: ModalFormPerfilProps) {

  const [form] = useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [perfil, setPerfil] = useState<Perfil>();
  const [permissoesTags, setPermissoesTags] = useState<string[]>([]);
  const [permissoesOptions, setPermissoesOptions] = useState<PermissaoOption[]>([]);

  function handleClose() {
    refetch();
    form.resetFields();
    setPerfil(undefined);
    setPermissoesOptions([]);
    setPermissoesTags([]);
    onClose();
  }

  async function getPerfil() {
    setLoading(true);
    try {
      const response = await http.get(`${URL.PERFIS.PERFIS_LISTA}/${perfilId}`);
      if (response.data) {
        const data = response.data;
        const permissoes = data?.permissoes?.map((p: PermissaoPerfil) => p.permissaoId);
        form.setFieldsValue({ nome: data.nome, descricao: data.descricao, permissoes });
        setPerfil(data);
        setPermissoesTags(permissoes);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  async function getPermissoes() {
    setLoading(true);
    try {
      const response = await http.get(URL.PERMISSOES.LISTA, {
        params: {
          size: 1000,
        },
      });
      if (response.data) {
        const data = response.data.sort((a: Permissao, b: Permissao) => a.descricao.localeCompare(b.descricao));
        const options = data?.map((p: Permissao) => ({ label: p.descricao, value: p.permissaoId }));
        setPermissoesOptions(options);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  function handlePermissoes(permissoes: string[]) {
    const permissoesAtuais = perfil?.permissoes;
    const adicionadas = permissoes.filter(p => !permissoesAtuais?.some(a => a.permissaoId === p));
    const removidas = permissoesAtuais?.map(p => p.permissaoId).filter(p => !permissoes.includes(p));
    return { adicionadas, removidas }
  }

  function handleRemovePermissao(id: string) {
    const permissoes = permissoesTags.filter(p => p !== id);
    form.setFieldsValue({ permissoes });
    setPermissoesTags(permissoes);
  }

  function getPermissaoName(id: string) {
    const permissao = permissoesOptions.find(p => p.value === id);
    return permissao?.label ?? "-";
  }

  async function handleCreate(values: any) {
    setLoading(true);
    try {
      const body = {
        nome: values.nome,
        descricao: values.descricao,
        permissaoIds: values.permissoes,
      }
      await http.post(URL.PERFIS.PERFIS_LISTA, body);
      showSuccess("Perfil de acesso criado com sucesso");
      handleClose();
    } catch (error) {
      console.log(error);
      showError("Erro ao criar perfil de acesso");
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdate(values: any) {
    setLoading(true);
    try {
      const { adicionadas, removidas } = handlePermissoes(values.permissoes);
      const body = {
        codigo: perfil?.codigo,
        nome: values.nome,
        descricao: values.descricao,
        permissaoIds: adicionadas,
        permissaoIdsRemovidas: removidas,
      }
      await http.put(`${URL.PERFIS.PERFIS_LISTA}/${perfilId}`, body);
      showSuccess("Perfil de acesso atualizado com sucesso");
      handleClose();
    } catch (error) {
      console.log(error);
      showError("Erro ao atualizar perfil de acesso");
    } finally {
      setLoading(false);
    }
  }

  function onValuesChange(changedValues: any) {
    if ("permissoes" in changedValues) {
      setPermissoesTags(changedValues.permissoes);
    }
  }

  useEffect(() => {
    if (isOpen) {
      getPermissoes();
    }
    if (isOpen && perfilId) {
      getPerfil();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, perfilId]);

  return (
    <StyledModal
      width={"80%"}
      visible={isOpen}
      onCancel={handleClose}
      afterClose={handleClose}
      title={`${perfilId ? "Editar" : "Criar"} Perfil`}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onValuesChange={onValuesChange}
        onFinish={perfilId ? handleUpdate : handleCreate}
      >
        <Row gutter={[8, 8]}>
          <Col span={8}>
            <Item
              label="Nome do Perfil"
              name="nome"
              rules={[
                { required: true, message: "Nome do Perfil é obrigatório" }
              ]}
            >
              <Input
                size="large"
                placeholder="Nome do Perfil"
                style={{ borderRadius: 10, width: "100%" }}
              />
            </Item>
          </Col>
          <Col span={16}>
            <Item
              label="Descrição"
              name="descricao"
              rules={[
                { required: true, message: "Descrição é obrigatório" }
              ]}
            >
              <Input
                size="large"
                placeholder="Descrição"
                style={{ borderRadius: 10, width: "100%" }}
              />
            </Item>
          </Col>
        </Row>
        <Item
          label="Permissões"
          name="permissoes"
          rules={[
            { required: true, message: "Selecione no mínimo uma permissão" }
          ]}
        >
          <StyledSelect
            allowClear
            showSearch
            mode="multiple"
            optionFilterProp="label"
            autoClearSearchValue={false}
            size="large"
            loading={loading}
            maxTagCount={0}
            maxTagPlaceholder={(values) => `${values.length} permissões`}
            options={permissoesOptions}
            placeholder="Permissões"
          />
        </Item>

        <Tags>
          {permissoesTags.map((item, index) => (
            <Tag
              key={index}
              color="processing"
              style={{ cursor: "pointer", margin: 0 }}
              onClick={() => handleRemovePermissao(item)}
            >
              {getPermissaoName(item)} <b>X</b>
            </Tag>
          ))}
        </Tags>

        <Flex justifyContent="end" gridGap={2}>
          <StyledButton
            size="large"
            loading={loading}
            onClick={handleClose}
            icon={<MdClose />}
          >Cancelar</StyledButton>
          <StyledButton
            type="primary"
            size="large"
            loading={loading}
            htmlType="submit"
            icon={<FaSave />}
          >Salvar</StyledButton>
        </Flex>
      </Form>
    </StyledModal>
  );
}

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 10px;
  }

  .ant-modal-header {
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    border-radius: 10px !important;
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
`;

const Tags = styled.div`
  max-height: min(30vh, 280px);
  overflow-y: scroll;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 16px;
`;

export default ModalFormPerfil;
